import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';

export default function UserGovernance() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = useAuth();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/users', {
        headers: { Authorization: `Bearer ${auth?.accessToken}` }
      });
      setUsers(response.data.users || []);
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error("Failed to fetch users", err);
        setError('Failed to load users.');
      }
    } finally {
      setLoading(false);
    }
  }, [auth?.accessToken]);

  useEffect(() => {
    if (auth?.isSessionReady && auth?.accessToken) {
      fetchUsers();
    } else if (auth?.isSessionReady && !auth?.accessToken) {
      setLoading(false);
      setError('Not authenticated.');
    }
  }, [auth?.isSessionReady, auth?.accessToken, fetchUsers]);

  const toggleStatus = async (id, currentStatus) => {
    try {
      const endpoint = currentStatus === 'active' ? 'suspend' : 'activate';
      await api.post(`/admin/users/${id}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${auth?.accessToken}` }
      });
      setUsers((s) => s.map((u) => (u.id === id ? { ...u, account_status: currentStatus === 'active' ? 'suspended' : 'active' } : u)));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="bg-white dark:bg-brand-dark text-slate-900 dark:text-brand-text min-h-full p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">User Governance</h1>
            <p className="text-slate-600 dark:text-slate-400">Manage registered system users and their account flags.</p>
          </div>
          <button
            onClick={fetchUsers}
            title="Reload data"
            className="p-2 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center justify-center self-start md:self-auto"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        <div className="overflow-x-auto bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-lg">
          <table className="min-w-full text-left">
            <thead>
              <tr className="text-sm text-slate-500 dark:text-slate-400">
                <th className="px-4 py-3">Full Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Stored Account Index</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-slate-600 dark:text-slate-400">
                    Loading users...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-red-600 dark:text-red-400">
                    {error}
                  </td>
                </tr>
              ) : users.length > 0 ? (
                users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-200 dark:border-slate-800">
                    <td className="px-4 py-3 font-medium">{u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username}</td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{u.email}</td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{u.accountIndex === null ? 'Shared' : u.accountIndex || u.id}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.account_status === 'active' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
                        {u.account_status || u.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleStatus(u.id, u.account_status || (u.status || '').toLowerCase())}
                        className={`px-3 py-1 rounded text-sm ${u.account_status === 'active' ? 'bg-red-700 text-white hover:bg-red-800' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                      >
                        {u.account_status === 'active' ? 'Suspend' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-slate-600 dark:text-slate-400">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
